// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	createNamedStringLiteralUnion,
	SnowflakeStringType,
	withOpenApiType,
} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

export const CosmeticItemType = withOpenApiType(
	createNamedStringLiteralUnion(
		[
			['avatar_decoration', 'avatar_decoration', 'A frame drawn around the avatar'],
			['avatar_ultra', 'avatar_ultra', 'Large decoration drawn behind or in front of the avatar, such as wings'],
			['profile_effect', 'profile_effect', 'An effect played over the profile card'],
			['nameplate', 'nameplate', 'A background behind the name in member and direct message lists'],
			['room_character', 'room_character', 'A 3D character for voice rooms'],
			['room_face', 'room_face', 'Face and expression of the modular voice room character'],
			['room_hair', 'room_hair', 'Hair of the modular voice room character'],
			['room_top', 'room_top', 'Shirt or jacket of the modular voice room character'],
			['room_bottom', 'room_bottom', 'Trousers or shorts of the modular voice room character'],
			['room_shoes', 'room_shoes', 'Shoes or socks of the modular voice room character'],
			['room_costume', 'room_costume', 'Costume of the modular voice room character, worn instead of top and bottom'],
			['room_hat', 'room_hat', 'Hat of the modular voice room character'],
			['room_glasses', 'room_glasses', 'Glasses of the modular voice room character'],
			['room_accessory', 'room_accessory', 'Accessory of the modular voice room character, such as headphones'],
			['room_emote', 'room_emote', 'A body emote the voice room character plays, such as a dance; owned, never worn'],
		],
		'The kind of profile cosmetic',
	),
	'CosmeticItemType',
);

export type CosmeticItemType = z.infer<typeof CosmeticItemType>;

export const CosmeticAvailability = withOpenApiType(
	createNamedStringLiteralUnion(
		[
			['free', 'free', 'Every account can wear it'],
			['premium', 'premium', 'Accounts with premium can wear it'],
			['owned', 'owned', 'Only accounts that own it can wear it'],
		],
		'Who can wear the cosmetic',
	),
	'CosmeticAvailability',
);

export type CosmeticAvailability = z.infer<typeof CosmeticAvailability>;

export const UltraLayoutSurface = z
	.object({
		width: z.number().min(0.5).max(6),
		height: z.number().min(0.5).max(6),
		offset_x: z.number().min(-3).max(3),
		offset_y: z.number().min(-3).max(3),
	})
	.describe('Multiples of the avatar diameter on the scale of the fixed sizes (2.30, 3.20, 3.40)');

export type UltraLayoutSurface = z.infer<typeof UltraLayoutSurface>;

export const UltraLayout = z.object({
	compact: UltraLayoutSurface.nullable().describe('Chat and member list; null uses the fixed size'),
	voice: UltraLayoutSurface.nullable().describe('Voice tiles; null uses the fixed size'),
	profile: UltraLayoutSurface.nullable().describe('Profile card; null uses the fixed size'),
});

export type UltraLayout = z.infer<typeof UltraLayout>;

export const UltraFront = z
	.object({
		closed: z.string().describe('Hash of the still closed frame'),
		open: z.array(z.string()).min(2).max(30).describe('Hashes of the opening frames in order; frame 0 matches closed'),
		idle: z.string().describe('Hash of the animated loop that plays while open'),
	})
	.describe(
		'Ultra decorations drawn in front of the avatar that open on interaction; every image is the left half and the client mirrors it',
	);

export type UltraFront = z.infer<typeof UltraFront>;

export const UltraReveal = z
	.object({
		closed: z.string().describe('Hash of the still closed frame'),
		intro: z.string().describe('Hash of the animated intro that plays once and holds its last frame'),
		intro_ms: z.number().int().describe('Length of the intro in milliseconds'),
		idle: z.string().describe('Hash of the animated loop that follows the intro'),
	})
	.describe(
		'Ultra decorations drawn whole in front of the avatar: rows show the closed frame, an open profile plays the intro and then the loop',
	);

export type UltraReveal = z.infer<typeof UltraReveal>;

export const EffectLayers = z.object({
	intro: z.string().describe('Hash of the animated intro that plays once'),
	loop: z.string().describe('Hash of the animated loop that follows the intro'),
	intro_ms: z.number().int().describe('Length of the intro in milliseconds'),
});

export type EffectLayers = z.infer<typeof EffectLayers>;

export const CosmeticItemResponse = z.object({
	id: SnowflakeStringType.describe('The ID of the cosmetic item'),
	type: CosmeticItemType,
	name: z.string().describe('The display name of the item'),
	description: z.string().nullable().describe('A short description of the item'),
	name_en: z.string().nullish().describe('The English name; null or absent: name in every language'),
	description_en: z
		.string()
		.nullish()
		.describe('The English description; null or absent: description in every language'),
	asset: z
		.string()
		.nullable()
		.describe(
			'Hash of the item image; the media proxy serves it under branding/cosmetic-<type>-<id>/<asset>. null for a room character part without its own image: clients draw the preview from the part manifest',
		),
	availability: CosmeticAvailability,
	ultra_layout: UltraLayout.nullish().describe('Ultra decorations only; null or absent uses the fixed sizes'),
	ultra_front: UltraFront.nullish().describe('Ultra decorations only; null or absent draws the item behind the avatar'),
	ultra_reveal: UltraReveal.nullish().describe(
		'Ultra decorations only; null or absent draws the item as ultra_front says. Never set together with ultra_front',
	),
	effect_layers: EffectLayers.nullish().describe('Profile effects only; null or absent plays the main image'),
	model_key: z
		.string()
		.nullish()
		.describe(
			'Room characters, room character parts and room emotes only: the key from the voice room character, part or emote manifest',
		),
});

export type CosmeticItemResponse = z.infer<typeof CosmeticItemResponse>;

export const CosmeticCatalogResponse = z.array(CosmeticItemResponse).describe('Catalog items, filtered by type');

export type CosmeticCatalogResponse = z.infer<typeof CosmeticCatalogResponse>;

export const CosmeticItemListResponse = z
	.array(CosmeticItemResponse)
	.describe('Every cosmetic item, items taken off sale included');

export type CosmeticItemListResponse = z.infer<typeof CosmeticItemListResponse>;

export const AdminCosmeticItemListResponse = z
	.array(CosmeticItemResponse.extend({on_sale: z.boolean().describe('Whether the item is in the on-sale catalog')}))
	.describe('Every cosmetic item with its sale membership, for administrators');

export type AdminCosmeticItemListResponse = z.infer<typeof AdminCosmeticItemListResponse>;

export const RoomPartsImportResponse = z.object({
	items: CosmeticItemListResponse.describe('Created or repaired room character parts'),
	skipped_faces: z
		.array(
			z.object({
				item_id: SnowflakeStringType,
				bundle_ids: z.array(SnowflakeStringType),
			}),
		)
		.describe('Faces left unchanged because a shop bundle contains them'),
});

export type RoomPartsImportResponse = z.infer<typeof RoomPartsImportResponse>;

export const EquippedCosmeticsResponse = z.object({
	avatar_decoration_id: SnowflakeStringType.nullable().describe('The avatar decoration being worn'),
	avatar_ultra_id: SnowflakeStringType.nullable().describe('The ultra decoration being worn'),
	profile_effect_id: SnowflakeStringType.nullable().describe('The profile effect being worn'),
	nameplate_id: SnowflakeStringType.nullable().describe('The nameplate being worn'),
});

export type EquippedCosmeticsResponse = z.infer<typeof EquippedCosmeticsResponse>;

const roomPartWorn = (part: string) => SnowflakeStringType.nullable().describe(`The room character ${part} being worn`);

export const RoomOutfitResponse = z.object({
	room_face_id: roomPartWorn('face'),
	room_hair_id: roomPartWorn('hair'),
	room_top_id: roomPartWorn('top'),
	room_bottom_id: roomPartWorn('bottom'),
	room_shoes_id: roomPartWorn('shoes'),
	room_costume_id: roomPartWorn('costume'),
	room_hat_id: roomPartWorn('hat'),
	room_glasses_id: roomPartWorn('glasses'),
	room_accessory_id: roomPartWorn('accessory'),
	room_character_id: SnowflakeStringType.nullable().describe(
		'The room character being worn; null is the default, or the worn parts when there are any',
	),
	room_character_shirt: z
		.number()
		.int()
		.nullable()
		.describe('Shirt colour (0xRRGGBB) of the default character and its parts; null derives it from the user ID'),
});

export type RoomOutfitResponse = z.infer<typeof RoomOutfitResponse>;

export const UserCosmeticsResponse = z.object({
	owned: z.array(CosmeticItemResponse).describe('Items the account owns'),
	equipped: EquippedCosmeticsResponse.extend(RoomOutfitResponse.shape).describe('Items the account wears'),
});

export type UserCosmeticsResponse = z.infer<typeof UserCosmeticsResponse>;

export const RoomCharacterEquipResponse = z.object({
	room_character_id: SnowflakeStringType.nullable().describe('The room character being worn; null is the default'),
	room_character_shirt: z
		.number()
		.int()
		.nullable()
		.describe('Shirt colour (0xRRGGBB) of the default room character; null derives it from the user ID'),
});

export type RoomCharacterEquipResponse = z.infer<typeof RoomCharacterEquipResponse>;

export const CosmeticAssetDataResponse = z.object({
	asset: z.string().describe('Hash of the worn item image'),
	item_id: SnowflakeStringType.describe('The ID of the worn cosmetic item'),
});

export type CosmeticAssetDataResponse = z.infer<typeof CosmeticAssetDataResponse>;

export const AdminUserCosmeticResponse = z.object({
	item_id: SnowflakeStringType.describe('The ID of the cosmetic item'),
	owned: z.boolean().describe('Whether the account owns the item now'),
	equipped: z.boolean().describe('Whether the account wears the item now'),
	changed: z.boolean().describe('false when the account already owned (grant) or did not own (take back) the item'),
	unequipped: z.boolean().describe('Take back only: the item was worn and has been taken off'),
});

export type AdminUserCosmeticResponse = z.infer<typeof AdminUserCosmeticResponse>;
